import { Component, OnInit, inject, signal, HostListener, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { OperationShiftService } from '../../services/operation-shift.service';
import { OperatorService } from 'src/app/modules/shared/services/operator.service';
import { ShiftService } from 'src/app/modules/shared/services/shift.service';
import { lastValueFrom } from 'rxjs';
import { ModalService } from 'src/app/modules/shared/services/modal.service';
import { LoadingService } from 'src/app/core';
import { GlobalDepartmentService } from 'src/app/core/services/global-department.service';

@Component({
  selector: 'app-manual-sorting',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './manual-sorting.component.html',
  styleUrl: './manual-sorting.component.scss'
})
export class ManualSortingComponent implements OnInit {
  private operationShiftService = inject(OperationShiftService);
  operatorService = inject(OperatorService);
  shiftService = inject(ShiftService);
  modalService = inject(ModalService);
  loadingService = inject(LoadingService);
  globalDepartmentService = inject(GlobalDepartmentService);
  private elementRef = inject(ElementRef);

  activeTab = signal<'PENDING' | 'APPROVED' | 'REJECTED'>('PENDING');
  pendingOutputs = signal<any[]>([]);
  approvedOutputs = signal<any[]>([]);
  rejectedOutputs = signal<any[]>([]);
  loading = signal<boolean>(false);
  searchJobNo = signal<string>('');

  selectedOperatorId = signal<number | null>(null);
  selectedShiftId = signal<number | null>(null);
  
  showFilterDropdown = false;
  tempSelectedOperatorId: number | null = null;
  tempSelectedShiftId: number | null = null;

  isOperatorDropdownOpen = false;
  operatorSearchText = signal<string>('');

  selectedItemIds = signal<number[]>([]);

  selectedForApproval = signal<any[]>([]);
  approvalAction = signal<'APPROVED' | 'REJECTED'>('APPROVED');
  isApproving = signal<boolean>(false);
  approvedCount = signal<number>(0);

  showCustomRange = signal(false);
  selectedYearValue: number = new Date().getFullYear();
  selectedMonthValue: number = new Date().getMonth();
  manualStartDate: string = '';
  manualEndDate: string = '';

  years: number[] = [];
  months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  startDate: string = '';
  endDate: string = '';

  sortColumn = signal<string>('');
  sortDirection = signal<'asc' | 'desc'>('asc');

  ngOnInit(): void {
    this.populateYears();
    this.onMonthYearChange();
    
    const deptId = this.globalDepartmentService.getDepartmentId() ?? null;
    this.operatorService.getAll(deptId).subscribe();
    this.shiftService.getAll(deptId).subscribe();
  }

  toggleFilterDropdown() {
    this.showFilterDropdown = !this.showFilterDropdown;
    if (this.showFilterDropdown) {
      this.tempSelectedOperatorId = this.selectedOperatorId();
      this.tempSelectedShiftId = this.selectedShiftId();
      this.syncOperatorSearchText();
    } else {
      this.isOperatorDropdownOpen = false;
    }
  }

  syncOperatorSearchText() {
    if (this.tempSelectedOperatorId === null) {
      this.operatorSearchText.set('');
    } else {
      const operators = this.operatorService.all()[0]?.operators || [];
      const found = operators.find((op: any) => op.operatorId === this.tempSelectedOperatorId);
      this.operatorSearchText.set(found ? this.titleCase(found.operatorName) : '');
    }
  }

  private titleCase(str: string): string {
    if (!str) return '';
    return str.toLowerCase().split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
  }

  get filteredOperators(): any[] {
    const operators = this.operatorService.all()[0]?.operators || [];
    const query = this.operatorSearchText().toLowerCase().trim();
    if (!query) return operators;

    const selectedName = this.getSelectedOperatorName().toLowerCase().trim();
    if (this.tempSelectedOperatorId !== null && query === selectedName) {
      return operators;
    }

    return operators.filter((op: any) =>
      (op.operatorName && op.operatorName.toLowerCase().includes(query)) ||
      (op.employeeNo && op.employeeNo.toLowerCase().includes(query))
    );
  }

  getSelectedOperatorName(): string {
    if (this.tempSelectedOperatorId === null) return 'Any';
    const operators = this.operatorService.all()[0]?.operators || [];
    const found = operators.find((op: any) => op.operatorId === this.tempSelectedOperatorId);
    return found ? found.operatorName : 'Any';
  }

  onOperatorSearchChange(value: string) {
    this.operatorSearchText.set(value);
    this.isOperatorDropdownOpen = true;
    if (!value.trim()) {
      this.tempSelectedOperatorId = null;
    }
  }

  selectOperator(op: any | null, event?: MouseEvent) {
    if (event) {
        event.stopPropagation();
    }
    if (op === null) {
      this.tempSelectedOperatorId = null;
      this.operatorSearchText.set('');
    } else {
      this.tempSelectedOperatorId = op.operatorId;
      this.operatorSearchText.set(this.titleCase(op.operatorName));
    }
    this.isOperatorDropdownOpen = false;
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.showFilterDropdown) {
      const clickedInsideFilter = this.elementRef.nativeElement.querySelector('.filter-container')?.contains(event.target as Node);
      if (!clickedInsideFilter) {
        this.showFilterDropdown = false;
        this.isOperatorDropdownOpen = false;
      } else if (this.isOperatorDropdownOpen) {
        const clickedInsideOperator = this.elementRef.nativeElement.querySelector('.operator-dropdown-container')?.contains(event.target as Node);
        if (!clickedInsideOperator) {
          this.isOperatorDropdownOpen = false;
        }
      }
    }
  }

  applyFilters() {
    this.selectedOperatorId.set(this.tempSelectedOperatorId);
    this.selectedShiftId.set(this.tempSelectedShiftId);
    this.isOperatorDropdownOpen = false;
    this.showFilterDropdown = false;
    this.fetchData();
  }

  clearFilters() {
    this.tempSelectedOperatorId = null;
    this.tempSelectedShiftId = null;
    this.selectedOperatorId.set(null);
    this.selectedShiftId.set(null);
    this.isOperatorDropdownOpen = false;
    this.operatorSearchText.set('');
    this.showFilterDropdown = false;
    this.fetchData();
  }

  toggleSelection(id: number) {
    const selected = this.selectedItemIds();
    if (selected.includes(id)) {
      this.selectedItemIds.set(selected.filter(x => x !== id));
    } else {
      this.selectedItemIds.set([...selected, id]);
    }
  }

  toggleAll(event: any) {
    if (event.target.checked) {
      // const allIds = this.getCurrentData().map(item => item.id);
      // this.selectedItemIds.set(allIds);
      const selectableIds = this.getCurrentData()
        .filter(item => item?.jobStatus?.toLowerCase() === 'completed')
        .map(item => item.id);
      this.selectedItemIds.set(selectableIds);
    } else {
      this.selectedItemIds.set([]);
    }
  }

  isAllSelected(): boolean {
    // const data = this.getCurrentData();
    // return data.length > 0 && this.selectedItemIds().length === data.length;
    const selectableData = this.getCurrentData().filter(item => item?.jobStatus?.toLowerCase() === 'completed');
    return selectableData.length > 0 && this.selectedItemIds().length === selectableData.length;
  }

  isSomeSelected(): boolean {
    const selected = this.selectedItemIds().length;
    // return selected > 0 && selected < this.getCurrentData().length;
    const selectableData = this.getCurrentData().filter(item => item?.jobStatus?.toLowerCase() === 'completed');
    return selected > 0 && selected < selectableData.length;
  }

  approveSelected() {
    const ids = this.selectedItemIds();
    if (ids.length === 0) return;
    
    const itemsToApprove = this.pendingOutputs().filter(item => ids.includes(item.id));
    this.approvalAction.set('APPROVED');
    this.selectedForApproval.set(itemsToApprove);
    this.modalService.$$manualApproveConfirm.set(true);
  }

  rejectSelected() {
    const ids = this.selectedItemIds();
    if (ids.length === 0) return;
    
    const itemsToReject = this.pendingOutputs().filter(item => ids.includes(item.id));
    this.approvalAction.set('REJECTED');
    this.selectedForApproval.set(itemsToReject);
    this.modalService.$$manualApproveConfirm.set(true);
  }

  populateYears(): void {
    const startYear = 2024;
    this.years = Array.from({ length: 12 }, (_, i) => startYear + i);
  }

  onMonthYearChange(): void {
    const year = Number(this.selectedYearValue);
    const month = Number(this.selectedMonthValue);

    const start = new Date(year, month, 1);
    const end = new Date(year, month + 1, 0);

    this.startDate = this.formatDate(start);
    this.endDate = this.formatDate(end);
    this.fetchData();
  }

  toggleCustomRange(): void {
    this.showCustomRange.set(!this.showCustomRange());
    if (!this.showCustomRange()) {
      this.onMonthYearChange();
    } else {
      this.manualStartDate = this.startDate;
      this.manualEndDate = this.endDate;
    }
  }

  applyManualDateRange(): void {
    if (!this.manualStartDate || !this.manualEndDate) return;
    this.startDate = this.manualStartDate;
    this.endDate = this.manualEndDate;
    this.fetchData();
  }

  formatDate(date: Date): string {
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  }

  fetchData(): void {
    this.fetchOutputs(this.activeTab());
  }

  setActiveTab(tab: 'PENDING' | 'APPROVED' | 'REJECTED'): void {
    this.activeTab.set(tab);
    this.selectedItemIds.set([]); // Clear selection when switching tabs
    this.fetchData();
  }

  sortBy(column: string): void {
    if (this.sortColumn() === column) {
      this.sortDirection.set(this.sortDirection() === 'asc' ? 'desc' : 'asc');
    } else {
      this.sortColumn.set(column);
      this.sortDirection.set('asc');
    }
  }

  getCurrentData(): any[] {
    let data = this.activeTab() === 'PENDING' ? this.pendingOutputs() 
      : this.activeTab() === 'APPROVED' ? this.approvedOutputs() 
      : this.rejectedOutputs();
    const searchTerm = this.searchJobNo().toLowerCase().trim();
    if (searchTerm) {
      data = data.filter(item => item.jobNo && item.jobNo.toLowerCase().includes(searchTerm));
    }

    const column = this.sortColumn();
    const direction = this.sortDirection() === 'asc' ? 1 : -1;

    if (column) {
      data = [...data].sort((a, b) => {
        let valA = a[column];
        let valB = b[column];

        // Handle string comparisons with case insensitivity
        if (typeof valA === 'string') valA = valA.toLowerCase();
        if (typeof valB === 'string') valB = valB.toLowerCase();

        // Handle nulls/undefined to avoid crash
        if (!valA) valA = '';
        if (!valB) valB = '';

        if (valA < valB) return -1 * direction;
        if (valA > valB) return 1 * direction;
        return 0;
      });
    }

    return data;
  }

  fetchOutputs(status: 'PENDING' | 'APPROVED' | 'REJECTED'): void {
    this.loading.set(true);
    const params: any = {
      approvalStatus: status,
      startDate: this.startDate,
      endDate: this.endDate,
    };
    
    if (this.selectedOperatorId()) {
      params.operator = this.selectedOperatorId();
    }
    
    if (this.selectedShiftId()) {
      params.shift = this.selectedShiftId();
    }

    this.operationShiftService.getUnapprovedOutputs(params).subscribe({
      next: (res: any) => {
        // Handling various common response structures
        const data = res?.data?.data || res?.data || res || [];
        const arrayData = Array.isArray(data) ? data : [data];
        if (status === 'PENDING') {
          this.pendingOutputs.set(arrayData);
        } else if (status === 'APPROVED') {
          this.approvedOutputs.set(arrayData);
        } else {
          this.rejectedOutputs.set(arrayData);
        }
        this.loading.set(false);
      },
      error: (err) => {
        console.error(`Error fetching ${status} outputs`, err);
        this.loading.set(false);
      }
    });
  }

  approveItem(item: any): void {
    this.approvalAction.set('APPROVED');
    this.selectedForApproval.set([item]);
    this.modalService.$$manualApproveConfirm.set(true);
  }

  rejectItem(item: any): void {
    this.approvalAction.set('REJECTED');
    this.selectedForApproval.set([item]);
    this.modalService.$$manualApproveConfirm.set(true);
  }

  async confirmApproval() {
    this.isApproving.set(true);
    this.approvedCount.set(0);
    this.loadingService.set(true);

    const items = this.selectedForApproval();
    
    for (const item of items) {
      try {
        const action = this.approvalAction();
        await lastValueFrom(this.operationShiftService.approveManualOutput(item.id, action));
        this.approvedCount.set(this.approvedCount() + 1);
      } catch (err) {
        console.error(`Failed to process output ${item.id}`, err);
        // Continue even if one fails
      }
    }

    this.loadingService.set(false);
    this.isApproving.set(false);
    this.modalService.$$manualApproveConfirm.set(false);
    this.selectedForApproval.set([]);
    this.selectedItemIds.set([]);
    this.fetchData();
  }

  closeApproveModal() {
    if (this.isApproving()) return;
    this.modalService.$$manualApproveConfirm.set(false);
    this.selectedForApproval.set([]);
  }
}